import { ProjectFileTypeEnum, ProjectStatus } from '@cyberlabrs/helpers';
import { ProjectTotalEarningsDto, ProjectWithAssigneesDto, ProjectWithClientInfo } from '../commonTypes/acs/project.types';
import { Page, Pageable } from '../commonTypes/pageable.types';
import { ActivityLogWithUsers } from '../commonTypes/ActivityLogTypes/activityLog.types';
import {
    ActivityFeedWithUserDto,
    AddAssigneesDto,
    AssigneeDto,
    CreateProjectDto,
    ProjectAssigneeDto,
    ProjectDto,
    ProjectDtoRelV2,
    ProjectFileDto,
    ProjectFileWithPresignedUrlDto,
    ProjectPublicDto,
    ProjectRoleDto,
    UpdateAssigneeDto,
    UpdateProjectDto,
    UserProjectDto,
    ProjectAssigneeRateRowDto,
    ProjectAssigneeCurrencyRowDto,
    ProjectAssigneeAssignmentPeriodRowDto,
} from '../commonTypes/project.types';
import { diContainer } from '../libs/container';
import { ApiBaseClass, IServiceHttp } from './ApiBase';
import { AppConfig } from '../config/AppConfig';

export namespace ProjectApiRequest {
    export interface GetAll {}

    export interface Create {
        body: CreateProjectDto;
    }

    export interface GetProjectAssignees {
        query: Pageable;
        params: {
            projectId: string;
        };
    }

    export interface GetUsersByProjectId {
        params: {
            projectId: string;
        };
    }

    export interface GetRolesFromProject {}

    export interface AddAssignees {
        body: AddAssigneesDto;
    }

    export interface UpdateAssignee {
        params: {
            projectId: string;
            userSub: string;
        };
        body: UpdateAssigneeDto;
    }

    export interface GetAllClientsProjects {
        params: {
            clientId: string;
        };
    }

    export interface GetAllUserProjects {
        params: {
            userSub: string;
        };
    }

    export interface Delete {
        body: {
            userSub: string;
            projectId: string;
        };
    }

    export interface DeleteAssignee {
        params: {
            id: string;
            userSub: string;
        };
    }

    export interface Update {
        params: {
            id: string;
        };
        body: UpdateProjectDto;
    }

    export interface UpdateProjectStatus {
        params: {
            id: string;
        };
        body: {
            projectStatus: ProjectStatus;
        };
    }

    export interface GetAssigneeRole {
        params: {
            projectId: string;
        };
    }

    export interface GetOrganizationProjectsCount {
        query: {
            from: string;
            to: string;
        };
    }

    export interface GetProjectsFromOrganization {}

    export interface GetProjectsByName {
        query: {
            name: string;
        };
    }

    export interface GetProjectById {
        params: {
            id: string;
        };
    }

    export interface AddProjectAssignees {
        params: {
            id: string;
        };
        body: {
            assignees: AssigneeDto[];
        };
    }

    export interface GetProjectTotalEarnings {
        params: {
            projectId: string;
        };
    }

    export interface CreateNoteActivity {
        body: {
            projectId?: string;
            clientId: string;
            title: string;
            description: string;
            createdBy: string;
        };
    }

    export interface CreateMeetActivity {
        body: {
            projectId?: string;
            clientId: string;
            title: string;
            description: string;
            date: string;
            time?: string;
            link?: string;
            createdBy: string;
        };
    }

    export interface GetActivityFeedByProjectId {
        params: {
            projectId: string;
        };
    }

    export interface GetActivityFeedByClientId {
        params: {
            clientId: string;
        };
    }

    export interface CreateProjectFile {
        projectId: string;
        fileType: ProjectFileTypeEnum;
        file: File;
        name: string;
        accessToken: string | null;
        idToken: string | null;
    }

    export interface GetFilesByProjectId {
        params: {
            projectId: string;
        };
    }
    export interface GetActivityLogsByProjectId {
        params: {
            id: string;
        };
    }
    export interface GetClientMemberNumber {
        params: {
            clientId: string;
        };
    }

    export interface PostProjectAssigneesRateForPeriod {
        body: {
            from: string;
            to: string;
            projectId: string;
        };
    }

    export interface GetProjectAssigneeCurrencyHistory {
        params: {
            projectId: string;
        };
        query: {
            from: string;
            to: string;
        };
    }

    export interface GetProjectAssigneeAssignmentHistory {
        params: {
            projectId: string;
        };
        query: {
            from: string;
            to: string;
        };
    }
}

export namespace ProjectApiResponse {
    export interface GetAll {
        body: ProjectWithClientInfo[];
    }

    export interface GetProjectAssignees {
        body: Page<ProjectAssigneeDto>;
    }

    export interface GetUsersByProjectId {
        body: string[];
    }

    export interface GetRolesFromProject {
        body: ProjectRoleDto[];
    }

    export interface GetAllClientsProjects {
        body: ProjectDto[];
    }

    export interface GetAllUserProjects {
        body: UserProjectDto[];
    }

    export interface GetAssigneeRole {
        body: ProjectRoleDto[];
    }

    export interface GetOrganizationProjectsCount {
        body: {
            countProjects: number;
            createdAtCount: number;
        };
    }

    export interface GetProjectsFromOrganization {
        body: ProjectPublicDto[];
    }

    export interface GetProjectsByName {
        body: ProjectWithClientInfo[];
    }

    export interface GetProjectsByRole {
        body: ProjectDtoRelV2[];
    }

    export interface GetProjectById {
        body: ProjectWithAssigneesDto;
    }
    export interface Update {
        body: ProjectDto;
    }

    export interface UpdateProjectStatus {
        body: ProjectDto;
    }

    export interface UpdateAssignee {
        body: UpdateAssigneeDto;
    }

    export interface GetProjectTotalEarnings {
        body: ProjectTotalEarningsDto;
    }

    export interface CreateNoteActivity {
        body: {
            id: string;
            projectId: string;
            title: string;
            description: string;
            createdAt: string;
        };
    }

    export interface CreateMeetActivity {
        body: {
            id: string;
            projectId: string;
            title: string;
            description: string;
            date: string;
            time: string | null;
            link: string | null;
            createdAt: string;
        };
    }

    export interface GetActivityFeedByProjectId {
        body: ActivityFeedWithUserDto;
    }

    export interface GetActivityFeedByClientId {
        body: ActivityFeedWithUserDto;
    }

    export interface CreateProjectFile {
        body: ProjectFileDto;
    }

    export interface GetFilesByProjectId {
        body: ProjectFileWithPresignedUrlDto[];
    }
    export interface GetActivityLogsByProjectId {
        body: ActivityLogWithUsers[];
    }
    export interface GetClientMemberNumber {
        body: number;
    }

    export interface PostProjectAssigneesRateForPeriod {
        body: ProjectAssigneeRateRowDto[];
    }

    export interface GetProjectAssigneeCurrencyHistory {
        body: ProjectAssigneeCurrencyRowDto[];
    }

    export interface GetProjectAssigneeAssignmentHistory {
        body: ProjectAssigneeAssignmentPeriodRowDto[];
    }
}

export class ProjectApi {
    constructor(private readonly apiBase: ApiBaseClass) {}

    public getAllFromOrganization(rest: IServiceHttp<ProjectApiRequest.GetAll, ProjectApiResponse.GetAll>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'acs/v1/project.byUser',
        });
    }

    public create(rest: IServiceHttp<ProjectApiRequest.Create, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'prj/v1/project',
        });
    }

    public getProjectAssigneesByProject(rest: IServiceHttp<ProjectApiRequest.GetProjectAssignees, ProjectApiResponse.GetProjectAssignees>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'prj/v1/project-assignee/:projectId',
        });
    }

    public getUsersByProject(rest: IServiceHttp<ProjectApiRequest.GetUsersByProjectId, ProjectApiResponse.GetUsersByProjectId>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'prj/v1/project-assignee/subs/:projectId',
        });
    }

    public getRolesFromProject(rest: IServiceHttp<ProjectApiRequest.GetRolesFromProject, ProjectApiResponse.GetRolesFromProject>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'prj/v1/project-role',
        });
    }

    public addAssignees(rest: IServiceHttp<ProjectApiRequest.AddAssignees, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'prj/v1/project.addAssignees',
        });
    }

    public updateAssignee(rest: IServiceHttp<ProjectApiRequest.UpdateAssignee, ProjectApiResponse.UpdateAssignee>) {
        return this.apiBase.configure({
            ...rest,
            method: 'PATCH',
            url: 'prj/v1/project-assignee/projectId/:projectId/userSub/:userSub',
        });
    }

    public getAllClientsProjects(rest: IServiceHttp<ProjectApiRequest.GetAllClientsProjects, ProjectApiResponse.GetAll>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'prj/v1/project/client/:clientId',
        });
    }

    public getAllActiveClientsProjects(rest: IServiceHttp<ProjectApiRequest.GetAllClientsProjects, ProjectApiResponse.GetAll>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'prj/v1/project/client/:clientId/active',
        });
    }

    public getUserProjects(rest: IServiceHttp<ProjectApiRequest.GetAllUserProjects, ProjectApiResponse.GetAllUserProjects>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'prj/v1/project.ofUser/:userSub',
        });
    }

    public delete(rest: IServiceHttp<ProjectApiRequest.Delete, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'DELETE',
            url: 'prj/v1/project/id/:id',
        });
    }
    public update(rest: IServiceHttp<ProjectApiRequest.Update, ProjectApiResponse.Update>) {
        return this.apiBase.configure({
            ...rest,
            method: 'PATCH',
            url: 'prj/v1/project/id/:id',
        });
    }

    public updateProjectStatus(rest: IServiceHttp<ProjectApiRequest.UpdateProjectStatus, ProjectApiResponse.UpdateProjectStatus>) {
        return this.apiBase.configure({
            ...rest,
            method: 'PATCH',
            url: 'prj/v1/project/id/:id/-updateStatus',
        });
    }

    public deleteProjectAssignee(rest: IServiceHttp<ProjectApiRequest.DeleteAssignee, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'prj/v1/project/id/:id/-removeAssignee/userSub/:userSub',
        });
    }

    public getAssigneeRole(rest: IServiceHttp<ProjectApiRequest.GetAssigneeRole, ProjectApiResponse.GetAssigneeRole>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'prj/v1/project-assignee/roles/:projectId',
        });
    }

    public getOrganizationProjectsCount(
        rest: IServiceHttp<ProjectApiRequest.GetOrganizationProjectsCount, ProjectApiResponse.GetOrganizationProjectsCount>
    ) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'prj/v1/project/organization-projects-count',
        });
    }

    public getProjectsFromOrganization(
        rest: IServiceHttp<ProjectApiRequest.GetProjectsFromOrganization, ProjectApiResponse.GetProjectsFromOrganization>
    ) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'prj/v1/project.byOrganizationId',
        });
    }

    public getProjectsByRole(rest: IServiceHttp<ProjectApiRequest.GetProjectsByName, ProjectApiResponse.GetProjectsByRole>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'prj/v1/project',
        });
    }

    public getProjectById(rest: IServiceHttp<ProjectApiRequest.GetProjectById, ProjectApiResponse.GetProjectById>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'prj/v1/project/id/:id',
        });
    }

    public addProjectAssignees(rest: IServiceHttp<ProjectApiRequest.AddProjectAssignees, void>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'prj/v1/project/id/:id/-addAssignees',
        });
    }

    public getProjectTotalEarnings(rest: IServiceHttp<ProjectApiRequest.GetProjectTotalEarnings, ProjectApiResponse.GetProjectTotalEarnings>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'acs/v1/project/:projectId/total-earnings',
        });
    }

    public getProjectAssigneeCurrencyHistory(
        rest: IServiceHttp<ProjectApiRequest.GetProjectAssigneeCurrencyHistory, ProjectApiResponse.GetProjectAssigneeCurrencyHistory>
    ) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'prj/v1/project/:projectId/assignee-currency-history',
        });
    }

    public getProjectAssigneeAssignmentHistory(
        rest: IServiceHttp<ProjectApiRequest.GetProjectAssigneeAssignmentHistory, ProjectApiResponse.GetProjectAssigneeAssignmentHistory>
    ) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'prj/v1/project/:projectId/assignee-assignment-history',
        });
    }

    public createNoteActivity(rest: IServiceHttp<ProjectApiRequest.CreateNoteActivity, ProjectApiResponse.CreateNoteActivity>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'prj/v1/activity-feed/note',
        });
    }

    public createMeetActivity(rest: IServiceHttp<ProjectApiRequest.CreateMeetActivity, ProjectApiResponse.CreateMeetActivity>) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'prj/v1/activity-feed/meet',
        });
    }

    public getActivityFeedByProjectId(
        rest: IServiceHttp<ProjectApiRequest.GetActivityFeedByProjectId, ProjectApiResponse.GetActivityFeedByProjectId>
    ) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'prj/v1/activity-feed/:projectId',
        });
    }

    public getActivityFeedByClientId(rest: IServiceHttp<ProjectApiRequest.GetActivityFeedByClientId, ProjectApiResponse.GetActivityFeedByClientId>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'prj/v1/activity-feed/client/:clientId',
        });
    }

    public createProjectFile(data: ProjectApiRequest.CreateProjectFile): Promise<ProjectApiResponse.CreateProjectFile> {
        const headers = new Headers();
        headers.append('X-Id-Token', data.idToken ?? '');
        headers.append('Authorization', `Bearer ${data.accessToken ?? ''}`);
        const formData = new FormData();
        const encodedFileName = encodeURIComponent(data.file.name);
        formData.append('document', data.file, encodedFileName);
        formData.append('name', data.name);
        formData.append('fileType', data.fileType);
        return fetch(`${AppConfig.serverUrlBase}/prj/v1/project-file/projectId/${data.projectId}`, {
            method: 'POST',
            headers: headers,
            body: formData,
        }).then(async res => {
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw data;
            }
            return data;
        });
    }

    public getFilesForProject(rest: IServiceHttp<ProjectApiRequest.GetFilesByProjectId, ProjectApiResponse.GetFilesByProjectId>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'prj/v1/project-file/projectId/:projectId',
        });
    }
    public getActivityLogsByProjectId(
        rest: IServiceHttp<ProjectApiRequest.GetActivityLogsByProjectId, ProjectApiResponse.GetActivityLogsByProjectId>
    ) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'prj/v1/project/id/:id/-activityLogs',
        });
    }
    public getClientMemberNumber(rest: IServiceHttp<ProjectApiRequest.GetClientMemberNumber, ProjectApiResponse.GetClientMemberNumber>) {
        return this.apiBase.configure({
            ...rest,
            method: 'GET',
            url: 'prj/v1/project-assignee/clientId/:clientId/-count',
        });
    }

    public postProjectAssigneesRateForPeriod(
        rest: IServiceHttp<ProjectApiRequest.PostProjectAssigneesRateForPeriod, ProjectApiResponse.PostProjectAssigneesRateForPeriod>
    ) {
        return this.apiBase.configure({
            ...rest,
            method: 'POST',
            url: 'prj/v1/project-assignees/rate',
        });
    }
}

diContainer.bindAsDynamic(ProjectApi, c => new ProjectApi(c.get(ApiBaseClass)));
